import { ProjectCoreLang } from '@aipb/gulliver/castor/model';
import { CastorConfigRepository, SearchService } from '@aipb/gulliver/catalog/data-access';
import { Topic, TopicRegisterItem } from '@aipb/gulliver/catalog/model';
import { CatalogRouting } from '@aipb/gulliver/catalog/routing';
import { InsideLoadingComponent } from '@aipb/shared/ui/loading';
import { SnackbarService } from '@aipb/shared/util/snackbar';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { RouterRepository } from '@ngneat/elf-ng-router-store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { captureMessage } from '@sentry/angular';
import { combineLatest, mergeMap, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TopicItemComponent } from '../../components';
import { TOPICS } from '../../topic.tokens';
import { TopicRepository, TopicService } from './topic-single.state';

interface TopicSingleVM {
  loading: boolean;
  cores: ProjectCoreLang[];
  topic: Topic;
  coreId: string;
  topicRegisterItems: TopicRegisterItem[];
}

@UntilDestroy()
@Component({
  selector: 'cat-topic-single',
  templateUrl: './topic-single.component.html',
  styleUrls: ['./topic-single.component.scss'],
  providers: [TopicRepository, TopicService],
  standalone: true,
  imports: [CommonModule, TopicItemComponent, InsideLoadingComponent]
})
export class TopicSingleComponent implements OnInit {
  repository = inject(TopicRepository);
  private coreId$ = this.repository.coreId$;
  private service = inject(TopicService);
  private routerRepository = inject(RouterRepository);
  private search = inject(SearchService);
  private catalogRouting = inject(CatalogRouting);
  private snackbar = inject(SnackbarService);
  private castorConfigRepository = inject(CastorConfigRepository);
  private transloco = inject(TranslocoService);
  private topics = inject(TOPICS);
  private topic$ = this.routerRepository.selectParams('id').pipe(
    map((id) => {
      const topic = this.topics.find((topic) => topic.id === id);
      if (!topic) {
        this.snackbar.open(this.transloco.translate('catalog.topic.not_found', { id }), 'error');
        captureMessage(`Topic[${id}] does not exist`);
        this.catalogRouting.dashboardDefault();
      }

      return topic;
    }),
    untilDestroyed(this)
  );
  vm$: Observable<TopicSingleVM> = combineLatest({
    loading: this.repository.loading$,
    cores: this.castorConfigRepository.cores$,
    topic: this.topic$,
    coreId: this.coreId$,
    topicRegisterItems: this.repository.items$
  });

  ngOnInit() {
    combineLatest([this.topic$, this.coreId$])
      .pipe(
        filter(([topic, coreId]) => {
          return !!topic && !!coreId;
        }),
        mergeMap(([topic, coreId]) => this.service.fetch(topic, coreId)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  onSearch(topic: Topic, item: TopicRegisterItem): void {
    this.search.addTab(
      {
        title: item.label,
        solrSearchParams: {
          query: `${topic.fieldName}:"${item.label}"`,
          coreId: this.repository.coreId
        }
      },
      true
    );
    this.catalogRouting.searchResult();
  }
}
